import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, SidebarCollapse } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { useState, type ReactElement, type ReactNode } from 'react';
import { cn } from 'lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import ChatHistoryPage from 'pages/chat-history/chat-history-page';
import { SidebarConfigurePanel } from './sidebar-configure-panel';
import { isPathActive } from 'utils/is-path-active';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useAgentSelectionStore } from 'state/store/chat/use-agent-selection-store';
import { useSidebar } from 'components/ui/sidebar';
import { useTheme } from 'components/core/theme-provider';
import {
  createAgentChatNavigation,
  defaultCreateAgentChatPrompt,
} from 'features/chat/utils/create-agent-chat-navigation';
import type { AgentGroupBy } from './agents-group-menu';
import { SidebarAgentsNavIcon } from './icons/sidebar-rail-icons';
import {
  sidebarNavItemIconOnlyClassName,
  sidebarNavRowHeightClassName,
  sidebarSidePanelClassName,
} from './sidebar-nav-item-styles';
import seliseLogo from 'assets/images/selise-ai-logo-new.svg';
import seliseLogoDark from 'assets/images/selise-ai-logo-new-dark.svg';
import chatPlusIn from 'assets/images/chat-plus-in.svg';
import chatPlusInDark from 'assets/images/chat-plus-in-dark.svg';
import { NotificationBell } from 'features/notifications/components/NotificationBell';

interface SidebarChatsPanelProps {
  children: ReactNode;
  groupBy?: AgentGroupBy;
  onHide?: () => void;
  showHideTooltip?: boolean;
}

function sidebarToggleShortcut() {
  if (typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform)) {
    return '⌘B';
  }
  return 'Ctrl+B';
}

function SidebarToggleTooltip({
  label,
  side,
  enabled = true,
  children,
}: {
  label: string;
  side: 'bottom' | 'right';
  enabled?: boolean;
  children: ReactElement;
}) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip open={enabled ? undefined : false}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side={side}
          sideOffset={8}
          className="flex items-center gap-2 rounded-nav border-0 bg-grey-950 px-2.5 py-1.5 text-sm font-normal leading-heading text-true-white shadow-md"
        >
          <span>{label}</span>
          <span className="text-grey-300">{sidebarToggleShortcut()}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ShowChatsPanelButton({
  onShow,
  showTooltip = true,
}: {
  onShow: () => void;
  showTooltip?: boolean;
}) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const label = t('SHOW_SIDEBAR', { defaultValue: 'Show sidebar' });
  const iconColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  return (
    <SidebarToggleTooltip label={label} side="right" enabled={showTooltip}>
      <button
        type="button"
        aria-label={label}
        onClick={onShow}
        className={cn(
          'flex items-center rounded-nav hover:bg-grey-100 dark:hover:bg-sidebar-pill-dark',
          sidebarNavRowHeightClassName,
          sidebarNavItemIconOnlyClassName
        )}
      >
        <SidebarCollapse width={20} height={20} strokeWidth={1.5} className={iconColor} />
      </button>
    </SidebarToggleTooltip>
  );
}

export function SidebarChatsPanel({
  children,
  groupBy,
  onHide,
  showHideTooltip = true,
}: SidebarChatsPanelProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { theme } = useTheme();
  const { isSidebarOverlay, setOpenMobile } = useSidebar();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const user = useCommonStore((state) => state.user);
  const disableNewChat = useChatStore((state) => state.disableNewChat);
  const setNewChat = useChatStore((state) => state.setNewChat);
  const setIsIncognitoChat = useChatStore((state) => state.setIsIncognitoChat);
  const clearUserSelection = useAgentSelectionStore((state) => state.clearUserSelection);
  const searchLabel = t('SEARCH_NAV', { defaultValue: 'Search' });
  const isGroupByAgents = groupBy === 'agents';
  const primaryActionLabel = isGroupByAgents
    ? t('CREATE_AGENT', { defaultValue: 'Create agent' })
    : t('NEW_CHAT', { defaultValue: 'New Chat' });
  const isCreateAgentOpen = search.includes('createAgent=true');
  const isPrimaryActionOpen = isGroupByAgents
    ? pathname === '/chat' && isCreateAgentOpen
    : pathname === '/chat' && !isCreateAgentOpen;
  const textColor = theme === 'dark' ? 'text-grey-50' : 'text-grey-900';
  const hideSidebarLabel = t('HIDE_SIDEBAR', { defaultValue: 'Hide sidebar' });

  const prepareNewChat = () => {
    if (disableNewChat) return false;
    if (isSidebarOverlay) {
      setOpenMobile(false);
    }
    setNewChat(true);
    setIsIncognitoChat(false);
    if (user?.id) {
      clearUserSelection(user.id);
    }
    return true;
  };

  const handleNewChat = () => {
    if (!prepareNewChat()) return;
    navigate({ pathname: '/chat', search: '' });
  };

  const handleCreateAgent = () => {
    if (!prepareNewChat()) return;
    const language = i18n.language;
    const fallbackPrompt = defaultCreateAgentChatPrompt(language);
    const translatedPrompt = t('CREATE_AGENT_CHAT_DEFAULT_PROMPT', {
      defaultValue: fallbackPrompt,
    });
    const { to, state } = createAgentChatNavigation({
      language,
      translatedPrompt: typeof translatedPrompt === 'string' ? translatedPrompt : undefined,
    });
    navigate(to, { state });
  };

  const handleConversationOpen = () => {
    setIsSearchOpen(false);
    if (isSidebarOverlay) {
      setOpenMobile(false);
    }
  };

  return (
    <aside
      data-testid="sidebar-chats-panel"
      className={sidebarSidePanelClassName}
      aria-label={t('AGENTS') || 'Agents'}
    >
      <div className="flex shrink-0 flex-col gap-6 px-3 pt-6">
        <div className="flex min-h-10 items-center justify-between gap-2 pl-2 pr-1">
          <SidebarToggleTooltip label={hideSidebarLabel} side="right" enabled={showHideTooltip}>
            <button
              type="button"
              aria-label={hideSidebarLabel}
              onClick={onHide}
              className="flex h-10 shrink-0 items-center rounded-nav px-1 hover:bg-grey-100 dark:hover:bg-sidebar-pill-dark"
            >
              <img
                src={theme === 'dark' ? seliseLogoDark : seliseLogo}
                alt="logo"
                className="h-8 w-auto max-w-20 shrink-0"
              />
            </button>
          </SidebarToggleTooltip>
          <NotificationBell borderless />
        </div>
        <div className="flex flex-col gap-2.5">
          <button
            type="button"
            onClick={() => setIsSearchOpen(true)}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg border border-grey-100 bg-true-white px-3 text-sm leading-heading text-grey-400 dark:border-sidebar-border dark:bg-sidebar',
              sidebarNavRowHeightClassName
            )}
            aria-label={searchLabel}
          >
            <span className="flex size-6 shrink-0 items-center justify-center">
              <Search
                width={20}
                height={20}
                strokeWidth={1.35}
                data-testid="sidebar-search-icon"
                className={theme === 'dark' ? 'text-grey-50' : 'text-grey-600'}
              />
            </span>
            <span>{searchLabel}</span>
          </button>
          <button
            type="button"
            onClick={isGroupByAgents ? handleCreateAgent : handleNewChat}
            disabled={disableNewChat}
            aria-current={isPrimaryActionOpen ? 'page' : undefined}
            className={cn(
              'flex w-full items-center justify-center gap-3 rounded bg-grey-50 py-3.5 pl-2 pr-4 text-sm font-normal leading-heading transition-colors disabled:cursor-not-allowed disabled:opacity-50 dark:bg-sidebar-pill-dark',
              sidebarNavRowHeightClassName,
              textColor
            )}
          >
            {isGroupByAgents ? (
              <SidebarAgentsNavIcon className="shrink-0" />
            ) : (
              <img
                src={theme === 'dark' ? chatPlusInDark : chatPlusIn}
                alt=""
                width={20}
                height={20}
                data-testid="sidebar-new-chat-icon"
                className="size-5 shrink-0"
              />
            )}
            <span>{primaryActionLabel}</span>
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">{children}</div>

      <SidebarConfigurePanel />

      <Dialog open={isSearchOpen} onOpenChange={setIsSearchOpen}>
        <DialogContent
          aria-describedby={undefined}
          className="flex h-[70dvh] w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-4 sm:max-w-[720px] sm:p-6"
        >
          <DialogTitle className="sr-only">{searchLabel}</DialogTitle>
          <ChatHistoryPage onConversationOpen={handleConversationOpen} />
        </DialogContent>
      </Dialog>
    </aside>
  );
}
